import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { serviceBySlug } from "@/data/services";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = () =>
    api
      .get("/admin/bookings")
      .then((r) => setBookings(r.data))
      .catch(() => setBookings([]));

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((list) => list.map((b) => (b.id === id ? data : b)));
      toast.success(`Booking marked ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(null);
    }
  };

  const markPaid = async (id) => {
    setUpdating(id);
    try {
      const { data } = await api.patch(`/admin/bookings/${id}/payment`, { payment_status: "paid" });
      setBookings((list) => list.map((b) => (b.id === id ? data : b)));
      toast.success("Offline payment recorded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(null);
    }
  };

  const refundBooking = async (id, ref) => {
    if (!window.confirm(`Refund booking ${ref}? The full paid amount will be returned via Razorpay. This cannot be undone.`)) return;
    setUpdating(id);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/refund`);
      setBookings((list) => list.map((b) => (b.id === id ? data : b)));
      toast.success(`Refund processed — ${data.refund_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(null);
    }
  };

  return (
    <AdminLayout title="Booking Management" testId="admin-bookings">
      {bookings === null && (
        <div className="h-48 animate-pulse rounded-xl border border-slate-200 bg-white" data-testid="admin-bookings-loading" />
      )}
      {bookings !== null && bookings.length === 0 && (
        <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-600" data-testid="admin-bookings-empty">
          No bookings yet.
        </p>
      )}
      {bookings !== null && bookings.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-bookings-table">
            <thead>
              <tr className="border-b border-slate-200 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <th className="px-5 py-3 font-medium">Booking</th>
                <th className="px-5 py-3 font-medium">Customer</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Payment</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-booking-row-${b.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-medium text-slate-900">{serviceBySlug(b.service)?.title || b.service}</p>
                    <p className="font-mono text-[11px] text-slate-500">{b.booking_ref}</p>
                    {b.razorpay_order_id && (
                      <p className="mt-1 font-mono text-[10px] text-slate-400">Order: {b.razorpay_order_id}</p>
                    )}
                    {b.payment_id && (
                      <p className="font-mono text-[10px] text-slate-400">Payment: {b.payment_id}</p>
                    )}
                    {b.refund_id && (
                      <p className="font-mono text-[10px] text-slate-400">
                        Refund: {b.refund_id} · ₹{(b.refund_amount_paise || 0) / 100}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-slate-900">{b.name}</p>
                    <p className="text-xs text-slate-500">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{fmtDate(b.preferred_date || b.created_at)}</td>
                  <td className="px-5 py-4 font-medium text-slate-900">
                    {b.fee_paise > 0 ? `₹${b.fee_paise / 100}` : "Free"}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge
                      value={b.payment_status === "unpaid" && b.payment_method === "offline" ? "pay_at_desk" : b.payment_status}
                    />
                    {(b.payment_status === "unpaid" || b.payment_status === "failed") && b.fee_paise > 0 && (
                      <button
                        onClick={() => markPaid(b.id)}
                        disabled={updating === b.id}
                        data-testid={`admin-mark-paid-${b.id}`}
                        className="mt-1.5 block text-xs font-semibold text-green-700 hover:underline disabled:opacity-50"
                      >
                        Mark paid (desk)
                      </button>
                    )}
                    {b.payment_status === "paid" && b.payment_method === "online" && (
                      <button
                        onClick={() => refundBooking(b.id, b.booking_ref)}
                        disabled={updating === b.id}
                        data-testid={`admin-refund-${b.id}`}
                        className="mt-1.5 block text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                      >
                        Refund payment
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <select
                      value={b.status}
                      disabled={updating === b.id}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      data-testid={`admin-status-select-${b.id}`}
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-900 focus:outline-none"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s[0].toUpperCase() + s.slice(1)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
